// Service worker registration and the "update available" flow (web-only; iOS updates through the App Store).
//
// - `registerServiceWorker()` registers /sw.js in production builds only (`import.meta.env.PROD`); the Vite dev server
//   never runs the worker.
// - A new version installs in the background and waits; `onUpdateAvailable(cb)` is called once it is ready (also for a
//   worker already waiting at startup). `applyUpdate()` asks it to take over (`SKIP_WAITING`) and reloads the page
//   once it controls it (`controllerchange`), so the page and its assets always come from the same version.
// - The registration is checked for updates when the page becomes visible again (at most hourly).
// Callback API on purpose (no signals): the UI decides how to show the prompt.

export type UpdateCallback = () => void;

export interface RegisterOptions {
  /** Default: `import.meta.env.PROD`. */
  enabled?: boolean;
  /** Default: '/sw.js'. */
  url?: string;
  /** Default: '/'. */
  scope?: string;
  /** Default: `navigator.serviceWorker`. */
  container?: ServiceWorkerContainer;
  /** Default: `location.reload()`. */
  reload?: () => void;
  /** Default: `document` (visibility-based update checks); pass null to disable. */
  document?: Document | null;
}

const callbacks = new Set<UpdateCallback>();
let waitingWorker: ServiceWorker | undefined;
let registration: ServiceWorkerRegistration | undefined;
let container: ServiceWorkerContainer | undefined;
let reloadPage: () => void = () => location.reload();
let reloading = false;
let lastUpdateCheck = 0;
const updateCheckInterval = 3_600_000;

function notify(worker: ServiceWorker): void {
  waitingWorker = worker;
  for (const callback of [...callbacks]) callback();
}

/**
 * Calls `callback` when a new version is installed and waiting (immediately, asynchronously, if one already is).
 * Returns the unsubscribe function.
 */
export function onUpdateAvailable(callback: UpdateCallback): () => void {
  callbacks.add(callback);
  if (waitingWorker) queueMicrotask(() => callbacks.has(callback) && callback());
  return () => callbacks.delete(callback);
}

/** Whether a new version is waiting. */
export function isUpdateAvailable(): boolean {
  return waitingWorker !== undefined;
}

/** Activates the waiting version and reloads the page when it takes control. No-op without a waiting version. */
export function applyUpdate(): void {
  const worker = waitingWorker ?? registration?.waiting ?? undefined;
  if (!worker || !container) return;
  container.addEventListener('controllerchange', () => {
    if (reloading) return;
    reloading = true;
    reloadPage();
  });
  worker.postMessage({ type: 'SKIP_WAITING' });
}

function watch(installing: ServiceWorker | null, target: ServiceWorkerContainer): void {
  if (!installing) return;
  installing.addEventListener('statechange', () => {
    // A first install (no controller yet) is not an update.
    if (installing.state === 'installed' && target.controller) notify(installing);
  });
}

/** Registers the service worker (production only). Resolves to the registration, or undefined when not registered. */
export async function registerServiceWorker(options: RegisterOptions = {}): Promise<ServiceWorkerRegistration | undefined> {
  const enabled = options.enabled ?? import.meta.env.PROD;
  const target = options.container ?? (typeof navigator !== 'undefined' ? navigator.serviceWorker : undefined);
  if (!enabled || !target) return undefined;
  container = target;
  if (options.reload) reloadPage = options.reload;
  try {
    registration = await target.register(options.url ?? '/sw.js', { scope: options.scope ?? '/', updateViaCache: 'none' });
  } catch (error) {
    console.error('Service worker registration failed', error);
    return undefined;
  }
  const current = registration;
  if (current.waiting && target.controller) notify(current.waiting);
  watch(current.installing, target);
  current.addEventListener('updatefound', () => watch(current.installing, target));

  const doc = options.document === undefined ? (typeof document === 'undefined' ? null : document) : options.document;
  doc?.addEventListener('visibilitychange', () => {
    if (doc.visibilityState !== 'visible') return;
    const now = Date.now();
    if (now - lastUpdateCheck < updateCheckInterval) return;
    lastUpdateCheck = now;
    current.update().catch(() => undefined);
  });
  return current;
}

/** Test hook: forget the module state. */
export function resetServiceWorkerState(): void {
  callbacks.clear();
  waitingWorker = undefined;
  registration = undefined;
  container = undefined;
  reloadPage = () => location.reload();
  reloading = false;
  lastUpdateCheck = 0;
}
